<template>
  <div>
    <div v-if="!canChange" class="row">
      <div class="col-sm-6 col-sm-offset-3">
        <div class="alert alert-info">
          <strong>Note:</strong>
          Due to your existing contracts or payments you’re not able to change your business entity
          type.
          <br />
          <a
            href="#"
            class="f-500"
            data-toggle="popover"
            data-trigger="click"
            data-title="If you need to change your business settings"
            data-html="true"
            data-placement="bottom"
            data-content="If you need to change your business settings
            on an active contract, your client will need to cancel the
            current contract first.<br>You will then be able to change
            your business entity settings, and your client can then offer
            a new contract to you.<br><br>Please reach out to Worksome
            if you need any help or have questions."
          >
            <i class="zmdi zmdi-help"></i>
            Learn more
          </a>
        </div>
      </div>
    </div>

    <div class="form-group">
      <input type="hidden" name="business_entity" :value="businessEntityTypeCopy" />
      <span class="required-asterix">*&nbsp;&nbsp;</span>
      <label class="col-md-4 control-label">{{ $t('Select your business entity type') }}</label>
      <div class="col-md-8 m-t-10">
        <WdsRadioGroup v-model:checked="businessEntityTypeCopy" name="business_entity">
          <WdsRadio :disabled="!canChange" value="limited">
            <i class="input-helper"></i>
            {{ $t('Limited company (PSC)') }}
            <br />
            <span class="c-gray small">
              {{ $t('E.g. if you have a Personal Service Company setup.') }}
            </span>
          </WdsRadio>

          <WdsRadio :disabled="!canChange" value="sole_trader">
            <i class="input-helper"></i>
            {{ $t('Registered sole trader') }}
            <br />
            <span class="c-gray small">
              {{ $t('If you have a Sole Trader business setup with HMRC.') }}
            </span>
          </WdsRadio>

          <WdsRadio :disabled="!canChange" value="umbrella">
            <i class="input-helper"></i>
            {{ $t('Umbrella company') }}
            <br />
            <span class="c-gray small">
              {{
                $t(
                  'If you need to get paid as an employed contractor working under an Umbrella Company',
                )
              }}
            </span>
          </WdsRadio>
        </WdsRadioGroup>

        <div
          v-if="businessEntityTypeCopy === 'umbrella' && hasHireNotYetAccepted"
          class="form-group"
        >
          <div class="alert alert-warning m-r-20 m-t-10 p-l-15">
            <strong>Note:</strong>
            You have a draft contract based on a setup, where you're
            <u>not</u>
            using an Umbrella service. If you change your setup to use an Umbrella service, we will
            automatically update this contract to reflect your updated business setup.
          </div>
        </div>

        <div
          v-if="businessEntityTypeCopy === 'umbrella'"
          class="p-10 p-b-0 bgm-lightgray m-20 f-13 form-group"
        >
          <p>
            <span class="f-500">{{ $t('Tell us about your Umbrella company') }}</span>
            <span class="c-gray small">
              {{ $t('Another umbrella service that is not Worksome.') }}
            </span>
          </p>

          <div class="row">
            <div class="col-sm-12 col-lg-12">
              <div class="alert alert-warning">
                <div class="f-500">
                  Be aware that we only work with Umbrella companies that have been accredited by
                  either
                  <a href="https://www.fcsa.org.uk/members/" target="_blank">FSCA</a>
                  or
                  <a
                    href="https://www.professionalpassport.com/Approved-Providers/Approved-Providers"
                    target="_blank"
                  >
                    Professional Passport
                  </a>
                  .
                </div>
              </div>
            </div>
          </div>

          <p>
            {{ $t('Their company name') }} *
            <br />
            <WdsInput
              name="umbrella_company_name"
              :model-value="businessEntity?.data?.umbrella?.umbrella_company_name"
              :placeholder="$t('Your umbrella company')"
            />
          </p>

          <p>
            {{ $t('Their company reg. no.') }} *
            <br />
            <WdsInput
              name="umbrella_company_no"
              :model-value="businessEntity?.data?.umbrella?.umbrella_company_no"
              :placeholder="$t('Company reg. no.')"
            />
          </p>

          <p class="m-b-5">
            {{ $t('Their address') }} *
            <br />
            <WdsInput
              name="umbrella_address"
              :model-value="businessEntity?.data?.umbrella?.umbrella_address"
              :placeholder="$t('Address')"
            />
          </p>

          <div class="row m-b-10">
            <div class="col-xs-7">
              <WdsInput
                name="umbrella_city"
                :model-value="businessEntity?.data?.umbrella?.umbrella_city"
                :placeholder="$t('City')"
              />
            </div>

            <div class="col-xs-5">
              <WdsInput
                name="umbrella_zipcode"
                :model-value="businessEntity?.data?.umbrella?.umbrella_zipcode"
                :placeholder="$t('Postcode')"
              />
            </div>
          </div>

          <p>
            <select
              id="select_meta_country"
              class="form-control"
              :value="
                businessEntity?.data?.umbrella?.umbrella_country ||
                accountMarket.countries[0]?.toUpperCase()
              "
              name="umbrella_country"
            >
              <option value="">{{ $t('Select country') }}</option>
              <option v-for="key in Object.keys(countries)" :key="key" :value="key">
                {{ countries[key] }}
              </option>
            </select>
          </p>

          <p class="m-b-0">
            {{ $t(`Their ${vatTerminology} reg. no.`) }} *
            <br />
            <WdsInput
              name="umbrella_vat_no"
              :model-value="businessEntity?.data?.umbrella?.umbrella_vat_no"
              :placeholder="$t(`${vatTerminology} no.`)"
            />
          </p>

          <div v-if="hasValidBankDetails" class="alert alert-warning small m-t-20 p-15">
            {{
              $t(
                'Make sure to also update your bank details to those of your umbrella company before any payouts.',
              )
            }}
          </div>
        </div>

        <div class="m-t-10">
          <WdsRadio
            v-model:checked="businessEntityTypeCopy"
            name="business_entity"
            :disabled="!canChange"
            value="no_setup"
          >
            <i class="input-helper"></i>
            {{ $t('No setup yet') }}
            <br />
            <span class="c-gray small">{{ $t('If you are not working through a business') }}</span>
          </WdsRadio>
        </div>
      </div>
    </div>

    <div v-if="businessEntityTypeCopy === 'limited' || businessEntityTypeCopy === 'sole_trader'">
      <div v-if="businessEntityTypeCopy === 'limited'" class="form-group">
        <label class="col-md-4 control-label">{{ $t('Company reg. no.') }}</label>
        <div class="col-md-3">
          <WdsInput
            name="company_no"
            :model-value="businessEntity?.company_no"
            :placeholder="$t('Company reg. no.')"
            required
          />
        </div>
      </div>

      <div v-if="businessEntityTypeCopy === 'sole_trader'" class="form-group">
        <label class="col-md-4 control-label">{{ $t('Unique Tax Reference (UTR)') }}</label>
        <div class="col-md-3">
          <WdsInput
            name="business_entity_data[sole_trader_utr]"
            :model-value="businessEntity?.data?.sole_trader_utr"
            :placeholder="$t('UTR')"
            required
          />
        </div>
      </div>

      <div class="form-group" :title="$t('Business name')">
        <label class="col-md-4 control-label">{{ $t('Business name') }}</label>
        <div class="col-md-6">
          <WdsInput
            name="registered_company_name"
            :model-value="businessEntity?.company_name"
            :placeholder="$t('Enter name of your business')"
            required
          />
        </div>
      </div>

      <div class="form-group">
        <label class="col-md-4 control-label">
          {{ $t(`Are you registered for  ${vatTerminology} ?`) }}
        </label>
        <div class="col-md-6 m-t-10">
          <WdsRadioGroup v-model:checked="vatRegistered" name="is_vat_registered">
            <WdsRadio :value="false">
              <i class="input-helper"></i>
              <span class="f-500">{{ $t('No') }}</span>
              <span class="small m-l-10 c-gray">
                {{ $t(`I will be billing without ${vatTerminology}`) }}
              </span>
            </WdsRadio>

            <WdsRadio :value="true">
              <i class="input-helper"></i>
              <span class="f-500">{{ $t('Yes') }}</span>
              <span class="small m-l-10 c-gray">
                {{ $t(`I have a ${vatTerminology} registration`) }}
              </span>
            </WdsRadio>
          </WdsRadioGroup>
        </div>
      </div>

      <div v-if="vatRegistered" class="form-group">
        <label class="col-md-4 control-label">{{ $t(`${vatTerminology} no.`) }}</label>
        <div class="col-md-4">
          <WdsInput
            name="vat_no"
            :model-value="businessEntity?.tax_no"
            :placeholder="$t(`${vatTerminology} no.`)"
            required
          />
          <span class="small c-gray">
            {{ $t(`Required if your company is registered for ${vatTerminology}.`) }}
          </span>
        </div>
      </div>

      <div v-if="vatRegisteredIsChanged && doesNotHaveEmptyBill" class="row">
        <div class="col-sm-offset-4 col-sm-8">
          <div class="alert alert-warning small">
            <strong>{{ $t('Note') }}:</strong>
            {{
              $t(
                `Changing your ${vatTerminology} registration will only impact your future bills. Contact us if you need changes made to any of your existing bills.`,
              )
            }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="businessEntityTypeCopy === 'no_setup'">
      <div class="row">
        <div class="col-sm-6 col-sm-offset-4">
          <div class="alert alert-warning">
            <strong>Note:</strong>
            Some jobs in Worksome will require you to either have a registered business, be a
            registered sole trader or work through an umbrella service.
            <br />
            <a
              href="https://help.worksome.dk/en/articles/4190692-business-entity-setup-for-freelancers"
              target="_blank"
            >
              Check out our guide for your business entity setup.
              <i class="zmdi zmdi-open-in-new"></i>
            </a>
          </div>
        </div>
      </div>
    </div>

    <div v-if="businessEntityTypeCopy === 'sole_trader'">
      <div class="row">
        <div class="col-sm-6 col-sm-offset-4">
          <div class="alert alert-warning">
            <strong>Note:</strong>
            When taking jobs where you may be regarded as an Agency Worker, you cannot be contracted
            through your sole trader business.
            <br />
            You can optionally be paid as PAYE from Worksome (see below) or use an umbrella service.
          </div>
        </div>
      </div>
    </div>

    <hr />

    <div v-if="canPaymentScheme" class="form-group">
      <label class="col-md-4 control-label">{{ $t('PAYE preference') }}</label>

      <div class="col-md-6 m-t-10">
        {{ $t('Are you prepared to be taxed as an Employee with Worksome handling the taxes?') }}
        <span class="small c-gray">
          {{
            $t(`For some jobs in Worksome, you can only be paid through a
            payroll (PAYE) even if you have a Limited company.`)
          }}
        </span>

        <div class="m-t-10">
          <input type="hidden" name="business_entity_data[can_paye]" />
          <WdsCheckbox
            v-model:checked="canPaye"
            v-tooltip="
              `${
                businessEntityTypeCopy === 'umbrella'
                  ? 'If you use an Umbrella company as your business entity, then we don\'t support using PAYE.'
                  : ''
              }`
            "
            value="1"
            name="business_entity_data[can_paye]"
            :disabled="businessEntityTypeCopy === 'umbrella'"
          >
            <i class="input-helper"></i>
            {{ $t('Yes, I will work with PAYE on some contracts') }}
            <br />
            <span class="c-gray small">
              {{ $t('You will need to supply additional data when you enter a contract.') }}
              <a
                v-tooltip="
                  `With PAYE (Pay As You Earn) Worksome will pay your income tax and
                national insurance contributions. You will be asked to fill out the required
                data for us to complete your payments.`
                "
                :href="complianceEditUrl"
              >
                {{ $t('Learn more') }}
              </a>
            </span>
          </WdsCheckbox>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { WdsInput, WdsRadio, WdsCheckbox, WdsRadioGroup } from '@worksome/wds-components'

export default {
  name: 'FreelancerBusinessEntity',

  components: {
    WdsRadio,
    WdsRadioGroup,
    WdsInput,
    WdsCheckbox,
  },

  props: {
    accountMarketData: {
      type: String,
      required: true,
      default: '{}',
    },
    businessEntityType: {
      type: String,
      required: true,
    },
    businessEntityData: {
      type: String,
      required: true,
      default: '{}',
    },
    canChange: {
      type: Boolean,
      default: false,
    },
    canPaymentScheme: {
      type: Boolean,
      default: false,
    },
    isVatRegistered: {
      type: Boolean,
      default: false,
    },
    doesNotHaveEmptyBill: {
      type: Boolean,
      default: false,
    },
    hasHireNotYetAccepted: {
      type: Boolean,
      default: false,
    },
    hasValidBankDetails: {
      type: Boolean,
      default: false,
    },
    complianceEditUrl: {
      type: String,
      default: '',
    },
    countryData: {
      type: String,
      default: '{}',
    },
  },

  data() {
    return {
      businessEntity: JSON.parse(this.businessEntityData),
      businessEntityTypeCopy: this.businessEntityType,
      accountMarket: JSON.parse(this.accountMarketData),
      countries: JSON.parse(this.countryData),
      vatRegistered: this.isVatRegistered,
      canPaye: false,
      vatRegisteredIsChanged: false,
    }
  },

  computed: {
    vatTerminology() {
      return vatTerminology
    },
  },

  watch: {
    vatRegistered() {
      this.vatRegisteredIsChanged = this.vatRegistered !== this.isVatRegistered
    },
  },

  created() {
    this.canPaye = this.businessEntity?.data?.can_paye === '1'
  },
}
</script>

<style>
.wds-input-container {
  width: 100% !important;
}
</style>
